import type { FileUIPart } from "ai";
import { nanoid } from "nanoid";

// ============================================================================
// Shared, pure attachment helpers
//
// The blob-URL lifecycle (create / revoke) is used in several places:
// the global PromptInputProvider, the self-managed PromptInput, and the
// unmount cleanups of both. These helpers keep that logic in one place so the
// `URL.createObjectURL` / `URL.revokeObjectURL` pairing can't drift apart.
// ============================================================================

export type AttachmentFile = FileUIPart & { id: string };

/** Prefix identifying object URLs produced by `URL.createObjectURL`. */
export const BLOB_URL_PREFIX = "blob:";

/** Extracts pasted files without consuming ordinary text clipboard content. */
export function filesFromClipboard(
	items: DataTransferItemList | null | undefined,
): File[] {
	if (!items) {
		return [];
	}
	const files: File[] = [];
	for (const item of items) {
		if (item.kind !== "file") {
			continue;
		}
		const file = item.getAsFile();
		if (file) {
			files.push(file);
		}
	}
	return files;
}

/** Create an attachment entry, allocating a blob URL for the given file. */
function createAttachment(file: File): AttachmentFile {
	return {
		id: nanoid(),
		type: "file" as const,
		url: URL.createObjectURL(file),
		mediaType: file.type,
		filename: file.name,
	};
}

/** Create attachment entries for a list of files. */
export function createAttachments(files: File[]): AttachmentFile[] {
	return files.map(createAttachment);
}

/** Revoke every blob URL held by the given attachments (safe to call on unmount). */
export function revokeAttachmentUrls(
	files: readonly AttachmentFile[],
): void {
	for (const file of files) {
		if (file.url) {
			URL.revokeObjectURL(file.url);
		}
	}
}

/** Remove one attachment by id, revoking its blob URL. Returns the next list. */
export function removeAttachmentById(
	files: readonly AttachmentFile[],
	id: string,
): AttachmentFile[] {
	const found = files.find((file) => file.id === id);
	if (found?.url) {
		URL.revokeObjectURL(found.url);
	}
	return files.filter((file) => file.id !== id);
}

/** Revoke every blob URL and return an empty list. */
export function clearAttachments(
	files: readonly AttachmentFile[],
): AttachmentFile[] {
	revokeAttachmentUrls(files);
	return [];
}
